/**
 * Downscale + re-encode gambar di sisi klien SEBELUM upload (Canvas API,
 * native browser, tanpa dependency baru). Dipakai di semua titik upload
 * gambar user (branding, cover kelas/produk/bonus, komunitas, dll) supaya
 * file besar dari kamera/HP tidak dikirim mentah ke server lalu dikirim
 * mentah lagi ke setiap pengunjung.
 *
 * Fail-open selalu: environment tanpa `createImageBitmap`/canvas 2D context,
 * atau proses apa pun yang gagal, mengembalikan file asli — upload tidak
 * pernah diblokir oleh util ini.
 */

const SKIP_TYPES = new Set(["image/gif"]);

export interface PrepareImageOptions {
  /** Downscale bila sisi terpanjang gambar melebihi ini (px). */
  maxDimension?: number;
  /**
   * Konversi output ke WebP (kompresi lebih baik, support alpha). Default
   * true. Set `false` untuk gambar yang dikonsumsi pihak ketiga yang belum
   * tentu kenal WebP (mis. OG image untuk crawler media sosial).
   */
  convertToWebp?: boolean;
  /** Kualitas encode 0-1, dipakai untuk WebP/JPEG. Default 0.85. */
  quality?: number;
}

function withExtension(name: string, ext: string): string {
  const base = name.replace(/\.[^./\\]+$/, "");
  return `${base}.${ext}`;
}

export async function prepareImageForUpload(file: File, options: PrepareImageOptions = {}): Promise<File> {
  const { maxDimension, convertToWebp = true, quality = 0.85 } = options;

  // GIF: canvas cuma menangkap 1 frame — jangan pernah proses (animasi bisa mati diam-diam).
  if (SKIP_TYPES.has(file.type)) return file;
  if (typeof createImageBitmap !== "function") return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  const { width, height } = bitmap;
  const needsResize = maxDimension != null && (width > maxDimension || height > maxDimension);

  // Tidak perlu resize dan tidak minta konversi format → tidak ada kerjaan.
  if (!needsResize && !convertToWebp) {
    bitmap.close();
    return file;
  }

  const scale = needsResize ? maxDimension! / Math.max(width, height) : 1;
  const targetW = Math.max(1, Math.round(width * scale));
  const targetH = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  bitmap.close();

  const outputType = convertToWebp
    ? "image/webp"
    : file.type === "image/png" || file.type === "image/webp"
      ? file.type
      : "image/jpeg";

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, outputType, quality));
  if (!blob) return file;

  const outputName = convertToWebp ? withExtension(file.name, "webp") : file.name;
  return new File([blob], outputName, { type: blob.type });
}
